import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Play,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  Clock,
  FileText,
  Award,
  Download,
  Share2,
  ThumbsUp
} from 'lucide-react'
import VideoPlayer from '../../components/shared/VideoPlayer'
import DiscussionPanel from '../../components/features/discussions/DiscussionPanel'
import CertificateGenerator from '../../components/ui/CertificateGenerator'

// Interfaces
interface CourseData {
  _id: string
  title: string
  description: string
  instructor: string
  thumbnail: string
  duration: string
  isEnrolled: boolean
  progress: number
  courseType: 'single' | 'multi-lesson'
  videoUrl?: string
  sections: Section[]
  completedLessons: Array<{ lessonId: string, completedAt: string } | string>
  notes: Array<{ lessonId: string, content: string, updatedAt: string }>
}

interface Section {
  _id: string
  title: string
  lessons: Lesson[]
  order: number
}

interface Lesson {
  _id: string
  title: string
  description: string
  videoUrl: string
  duration: string
  isFreePreview: boolean
  order: number
}

export default function CourseLearn() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()

  // State
  const [courseData, setCourseData] = useState<CourseData | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'notes' | 'discussion'>('overview')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [videoProgress, setVideoProgress] = useState(0)
  const [lessonNotes, setLessonNotes] = useState('')
  const [lessonProgress, setLessonProgress] = useState<{ [key: string]: number }>({})

  // Load Course
  useEffect(() => {
    if (!courseId) return
    loadCourse()
  }, [courseId])

  const loadCourse = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        return
      }

      const response = await fetch(`/api/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response.ok) throw new Error('Failed to load course')

      const data = await response.json()
      const course = data.course
      if (course.instructor && course.instructor.toLowerCase() === 'sudha sharma') {
        course.instructor = 'Sudha'
      }
      setCourseData(course)

      if (course.sections?.length > 0 && course.sections[0].lessons?.length > 0) {
        setCurrentLesson(course.sections[0].lessons[0])
        setExpandedSections(new Set([course.sections[0]._id]))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Helpers
  const isLessonCompleted = (lessonId: string) => {
    if (!courseData) return false
    return courseData.completedLessons.some(item =>
      typeof item === 'string' ? item === lessonId : item.lessonId === lessonId
    )
  }

  const handleLessonProgress = (progress: number) => {
    setVideoProgress(progress)
    if (currentLesson) {
      setLessonProgress(prev => ({ ...prev, [currentLesson._id]: progress }))
    }
    if (progress >= 85 && currentLesson && !isLessonCompleted(currentLesson._id)) {
      handleLessonComplete()
    }
  }

  // Sync notes when lesson changes
  useEffect(() => {
    if (currentLesson && courseData?.notes) {
      const note = courseData.notes.find(n => n.lessonId === currentLesson._id)
      setLessonNotes(note ? note.content : '')
    } else {
      setLessonNotes('')
    }
  }, [currentLesson, courseData])

  const handleSaveNote = async () => {
    if (!currentLesson || !courseId) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/${courseId}/lessons/${currentLesson._id}/note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: lessonNotes })
      })

      if (response.ok) {
        const data = await response.json()
        setCourseData(prev => prev ? { ...prev, notes: data.notes } : null)
        alert('Note saved successfully!')
      } else {
        alert('Failed to save note')
      }
    } catch (error) {
      console.error('Error saving note:', error)
      alert('Error saving note')
    }
  }

  const handleLessonComplete = async () => {
    if (!currentLesson || !courseData || isLessonCompleted(currentLesson._id)) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/${courseId}/lessons/${currentLesson._id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ videoProgress: 100, watchTime: 0 })
      })

      if (response.ok) {
        setCourseData(prev => prev ? {
          ...prev,
          completedLessons: [...prev.completedLessons, { lessonId: currentLesson._id, completedAt: new Date().toISOString() }],
          progress: Math.min(prev.progress + (100 / (prev.sections.reduce((acc, s) => acc + s.lessons.length, 0) || 1)), 100)
        } : null)
      }
    } catch (error) {
      console.error('Error completing lesson:', error)
    }
  }

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) newExpanded.delete(sectionId)
    else newExpanded.add(sectionId)
    setExpandedSections(newExpanded)
  }

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>
  if (!courseData) return null

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* LEFT COLUMN (Video & Details) */}
        <div className="lg:col-span-2 space-y-6">

          {/* NAVIGATION BREADCRUMB */}
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={() => navigate('/user/courses')}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Courses
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium truncate max-w-[300px]">{courseData.title}</span>
          </div>

          {/* VIDEO PLAYER */}
          <div className="bg-black rounded-xl overflow-hidden shadow-lg aspect-video">
            {currentLesson?.videoUrl ? (
              <VideoPlayer
                videoUrl={currentLesson.videoUrl}
                title={currentLesson.title}
                onProgress={handleLessonProgress}
                onComplete={handleLessonComplete}
                isCompleted={isLessonCompleted(currentLesson._id)}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
                <p>Select a lesson to start watching</p>
              </div>
            )}
          </div>

          {/* LESSON TITLE & ACTIONS */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentLesson?.title}</h2>
              <p className="text-gray-500 text-sm">Lesson {currentLesson?.order} • {currentLesson?.duration}</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                <ThumbsUp className="w-4 h-4" /> Like
              </button>
              <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 py-4 text-sm font-medium text-center border-b-2 transition-colors ${activeTab === 'overview' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`flex-1 py-4 text-sm font-medium text-center border-b-2 transition-colors ${activeTab === 'notes' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Notes
              </button>
              <button
                onClick={() => setActiveTab('discussion')}
                className={`flex-1 py-4 text-sm font-medium text-center border-b-2 transition-colors ${activeTab === 'discussion' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Q&A
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>{currentLesson?.description || "No description available."}</p>

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructor</h3>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-xl">
                        {courseData.instructor.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{courseData.instructor}</p>
                        <p className="text-xs text-gray-500">Course Instructor</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <textarea
                    className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Type your notes here..."
                    value={lessonNotes}
                    onChange={(e) => setLessonNotes(e.target.value)}
                  />
                  <button
                    onClick={handleSaveNote}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                  >
                    Save Note
                  </button>
                </div>
              )}

              {activeTab === 'discussion' && currentLesson && (
                <DiscussionPanel
                  courseId={courseId || ''}
                  lessonId={currentLesson._id}
                  lessonTitle={currentLesson.title}
                  inline={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN (Course Content) */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden sticky top-6">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900">Course Content</h3>
                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                  {Math.round(courseData.progress)}% Done
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-600 rounded-full transition-all duration-500"
                  style={{ width: `${courseData.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
              {courseData.sections.map((section, idx) => (
                <div key={section._id} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() => toggleSection(section._id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-sm text-gray-900 text-left">
                      <span className="text-gray-500 mr-2">Section {idx + 1}:</span>
                      {section.title}
                    </div>
                    {expandedSections.has(section._id) ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>

                  {expandedSections.has(section._id) && (
                    <div className="bg-gray-50/50">
                      {section.lessons.map((lesson) => {
                        const isActive = currentLesson?._id === lesson._id
                        const isCompleted = isLessonCompleted(lesson._id)

                        return (
                          <button
                            key={lesson._id}
                            onClick={() => setCurrentLesson(lesson)}
                            className={`w-full p-3 pl-6 flex items-start gap-3 text-left transition-colors border-l-4 ${isActive
                              ? 'bg-purple-50 border-purple-600'
                              : 'hover:bg-gray-100 border-transparent'
                              }`}
                          >
                            <div className="mt-0.5">
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <div className={`w-4 h-4 rounded-full border-2 ${isActive ? 'border-purple-600' : 'border-gray-300'}`}></div>
                              )}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${isActive ? 'text-purple-700' : 'text-gray-700'}`}>
                                {lesson.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {lesson.duration}
                              </p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}